import React from "react";
import { type Control, type FieldPath, useWatch } from "react-hook-form";
import { Tooltip } from "@flixbus/honeycomb-react";
import { Icon, IconCopy } from "@flixbus/honeycomb-icons-react";
import type { AddCostFormValues } from "../../CostPanel.types";
import styles from "./CopyAcrossButton.module.scss";

interface CopyAcrossButtonProps {
  variableId: string;
  variableName: string;
  januaryFieldName: FieldPath<AddCostFormValues>;
  control: Control<AddCostFormValues>;
  onCopy: (variableId: string, januaryValue: string) => void;
}

// /\d/ mirrors the schema's not-lone-separator rule: a bare "." / "," isn't copyable.
const isCopyableValue = (value: unknown): value is string =>
  typeof value === "string" && value.trim() !== "" && /\d/.test(value);

const CopyAcrossButton: React.FC<CopyAcrossButtonProps> = ({
  variableId,
  variableName,
  januaryFieldName,
  control,
  onCopy,
}) => {
  const januaryValue = useWatch({ control, name: januaryFieldName });
  const copyableValue = isCopyableValue(januaryValue) ? januaryValue : null;
  const canCopy = copyableValue !== null;
  const tooltipId = `copy-across-${variableId}`;
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [tooltipActive, setTooltipActive] = React.useState(false);
  const button = (
    <button
      ref={buttonRef}
      type="button"
      className={styles.copyButton}
      aria-label={`Copy ${variableName} across all months`}
      aria-describedby={canCopy ? tooltipId : undefined}
      disabled={!canCopy}
      onClick={() =>
        copyableValue !== null && onCopy(variableId, copyableValue)
      }
      onMouseEnter={() => setTooltipActive(true)}
      onMouseLeave={() => setTooltipActive(false)}
      onFocus={() => setTooltipActive(true)}
      onBlur={() => setTooltipActive(false)}
    >
      <Icon InlineIcon={IconCopy} size={3} aria-hidden="true" />
    </button>
  );
  if (!canCopy) return button;
  return (
    <Tooltip
      id={tooltipId}
      content="Copy to all months"
      active={tooltipActive}
      disableInternalHandlers
      fixed
      targetRef={buttonRef}
      position="top"
      extraClasses={styles.copyTooltip}
    >
      {button}
    </Tooltip>
  );
};

export default React.memo(CopyAcrossButton);
